<template>
  <div class="segments-manager">
    <div class="level">
      <div class="level-left">
        <div class="level-item">
          <h2 class="subtitle">Episode Segments</h2>
        </div>
      </div>
      <div class="level-right">
        <div class="level-item">
          <b-button
            type="is-primary"
            icon-left="plus"
            :disabled="!episodeId"
            @click="openAddSegmentModal"
          >
            Add Segment
          </b-button>
        </div>
        <div class="level-item">
          <b-button
            type="is-info"
            icon-left="download"
            :disabled="segments.length === 0"
            @click="exportCSV"
          >
            Export CSV
          </b-button>
        </div>
      </div>
    </div>

    <b-table
      :data="segments"
      :loading="loading"
      :bordered="false"
      :striped="true"
      :narrowed="false"
      :hoverable="true"
      :mobile-cards="false"
      :paginated="segments.length > 10"
      :per-page="10"
      class="segments-table"
    >
      <template slot-scope="props">
        <b-table-column
          field="segment_number"
          label="Segment #"
          numeric
          sortable
        >
          {{ props.row.segment_number }}
        </b-table-column>

        <b-table-column field="segment_type" label="Type">
          <b-tag
            :type="
              props.row.segment_type === 'content' ? 'is-success' : 'is-warning'
            "
          >
            {{ props.row.segment_type }}
          </b-tag>
        </b-table-column>

        <b-table-column field="in_point" label="In Point">
          <code>{{ props.row.in_point }}</code>
        </b-table-column>

        <b-table-column field="out_point" label="Out Point">
          <code>{{ props.row.out_point }}</code>
        </b-table-column>

        <b-table-column field="duration_seconds" label="Duration">
          {{ formatDuration(props.row.duration_seconds) }}
        </b-table-column>

        <b-table-column field="frame_rate" label="FPS" numeric>
          {{ props.row.frame_rate }}
        </b-table-column>

        <b-table-column field="notes" label="Notes">
          <span class="is-size-7 has-text-grey">{{
            props.row.notes || '-'
          }}</span>
        </b-table-column>

        <b-table-column label="Actions">
          <b-button
            size="is-small"
            type="is-info"
            icon-left="pencil"
            @click="openEditSegmentModal(props.row)"
          />
          <b-button
            size="is-small"
            type="is-danger"
            icon-left="delete"
            @click="confirmDelete(props.row)"
          />
        </b-table-column>
      </template>

      <template #empty>
        <div class="has-text-centered">
          <p>No segments added yet</p>
          <p class="is-size-7 has-text-grey">
            Add segments to define in/out points for this episode
          </p>
        </div>
      </template>
    </b-table>

    <!-- Add/Edit Segment Modal -->
    <b-modal
      :active.sync="isModalActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      :can-cancel="['escape', 'x', 'outside']"
      aria-role="dialog"
      aria-modal
    >
      <template #default="props">
        <div class="modal-card" style="width: 600px">
          <header class="modal-card-head">
            <p class="modal-card-title">
              {{ editingSegment ? 'Edit Segment' : 'Add Segment' }}
            </p>
            <button type="button" class="delete" @click="closeModal" />
          </header>
          <section class="modal-card-body">
            <ValidationObserver ref="segmentObserver" v-slot="{ passes }">
              <form @submit.prevent="passes(saveSegment)">
                <div class="columns is-multiline">
                  <div class="column is-6">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="Segment Number"
                      rules="required|min_value:1"
                    >
                      <b-field
                        label="Segment Number"
                        :type="{ 'is-danger': errors.length > 0 }"
                        :message="errors[0]"
                      >
                        <b-input
                          v-model.number="segmentForm.segmentNumber"
                          type="number"
                          min="1"
                        />
                      </b-field>
                    </ValidationProvider>
                  </div>

                  <div class="column is-6">
                    <b-field label="Segment Type">
                      <b-select v-model="segmentForm.segmentType" expanded>
                        <option value="content">Content</option>
                        <option value="commercial">Commercial</option>
                        <option value="promo">Promo</option>
                        <option value="other">Other</option>
                      </b-select>
                    </b-field>
                  </div>

                  <div class="column is-6">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="In Point"
                      rules="required|timecode"
                    >
                      <b-field
                        label="In Point (HH:MM:SS:FF)"
                        :type="{ 'is-danger': errors.length > 0 }"
                        :message="errors[0]"
                      >
                        <b-input
                          v-model="segmentForm.inPoint"
                          placeholder="00:00:00:00"
                          @input="validateTimecode"
                        />
                      </b-field>
                    </ValidationProvider>
                    <p class="help">
                      Format: Hours:Minutes:Seconds:Frames (e.g., 01:23:45:12)
                    </p>
                  </div>

                  <div class="column is-6">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="Out Point"
                      rules="required|timecode"
                    >
                      <b-field
                        label="Out Point (HH:MM:SS:FF)"
                        :type="{ 'is-danger': errors.length > 0 }"
                        :message="errors[0]"
                      >
                        <b-input
                          v-model="segmentForm.outPoint"
                          placeholder="00:00:00:00"
                          @input="validateTimecode"
                        />
                      </b-field>
                    </ValidationProvider>
                    <p class="help">
                      Format: Hours:Minutes:Seconds:Frames (e.g., 01:25:30:24)
                    </p>
                  </div>

                  <div class="column is-6">
                    <b-field label="Frame Rate (FPS)">
                      <b-select v-model.number="segmentForm.frameRate" expanded>
                        <option :value="23.976">23.976</option>
                        <option :value="24">24</option>
                        <option :value="25">25 (PAL)</option>
                        <option :value="29.97">29.97 (NTSC)</option>
                        <option :value="30">30</option>
                        <option :value="50">50</option>
                        <option :value="59.94">59.94</option>
                        <option :value="60">60</option>
                      </b-select>
                    </b-field>
                  </div>

                  <div class="column is-12">
                    <b-field label="Notes (Optional)">
                      <b-input
                        v-model="segmentForm.notes"
                        type="textarea"
                        placeholder="Additional notes about this segment..."
                      />
                    </b-field>
                  </div>
                </div>
              </form>
            </ValidationObserver>
          </section>
          <footer class="modal-card-foot">
            <b-button @click="closeModal">Cancel</b-button>
            <b-button type="is-primary" @click="saveSegment">Save</b-button>
          </footer>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import segmentsAPI from '@/api/segments'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import logger from '@/utils/logger'

export default {
  name: 'SegmentsManager',
  components: {
    ValidationObserver,
    ValidationProvider
  },
  props: {
    episodeId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      segments: [],
      loading: false,
      isModalActive: false,
      editingSegment: null,
      segmentForm: {
        segmentNumber: 1,
        segmentType: 'content',
        inPoint: '00:00:00:00',
        outPoint: '00:00:00:00',
        frameRate: 25,
        notes: ''
      }
    }
  },
  watch: {
    episodeId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadSegments()
        }
      }
    },
    isModalActive(newVal, oldVal) {
      logger.debug('isModalActive changed to:', newVal)
      // If modal was closed externally (escape, outside click), reset form and validation
      if (!newVal && oldVal) {
        this.editingSegment = null
        this.segmentForm = {
          segmentNumber: 1,
          segmentType: 'content',
          inPoint: '00:00:00:00',
          outPoint: '00:00:00:00',
          frameRate: 25,
          notes: ''
        }
        // Reset validation in next tick to ensure refs are available
        this.$nextTick(() => {
          if (this.$refs.segmentObserver) {
            this.$refs.segmentObserver.reset()
          }
        })
      }
    }
  },
  mounted() {
    logger.debug('SegmentsManager mounted, episodeId:', this.episodeId)
  },
  methods: {
    async loadSegments() {
      if (!this.episodeId) return

      this.loading = true
      try {
        this.segments = await segmentsAPI.getAllByEpisodeId(this.episodeId)
        logger.debug('Loaded segments:', this.segments)
        logger.debug('Number of segments:', this.segments.length)
        if (this.segments.length > 0) {
          logger.debug('First segment data:', this.segments[0])
          logger.debug('First segment in_point:', this.segments[0].in_point)
          logger.debug('First segment out_point:', this.segments[0].out_point)
          logger.debug('First segment frame_rate:', this.segments[0].frame_rate)
          logger.debug(
            'First segment duration_seconds:',
            this.segments[0].duration_seconds
          )
        }
      } catch (error) {
        logger.error('Failed to load segments:', error)
        this.$buefy.toast.open({
          message: 'Failed to load segments',
          type: 'is-danger'
        })
      } finally {
        this.loading = false
      }
    },

    openAddSegmentModal() {
      logger.debug('Opening add segment modal, episodeId:', this.episodeId)
      this.editingSegment = null
      this.segmentForm = {
        segmentNumber: this.segments.length + 1,
        segmentType: 'content',
        inPoint: '00:00:00:00',
        outPoint: '00:00:00:00',
        frameRate: 25,
        notes: ''
      }
      logger.debug('Setting isModalActive to true')
      this.isModalActive = true
      logger.debug('isModalActive is now:', this.isModalActive)

      // Force Vue to update
      this.$nextTick(() => {
        logger.debug('After nextTick, isModalActive:', this.isModalActive)
      })
    },

    openEditSegmentModal(segment) {
      this.editingSegment = segment
      this.segmentForm = {
        segmentNumber: segment.segment_number,
        segmentType: segment.segment_type,
        inPoint: segment.in_point,
        outPoint: segment.out_point,
        frameRate: segment.frame_rate,
        notes: segment.notes || ''
      }
      this.isModalActive = true
    },

    closeModal() {
      this.isModalActive = false
      this.editingSegment = null
      // Reset form to default values
      this.segmentForm = {
        segmentNumber: 1,
        segmentType: 'content',
        inPoint: '00:00:00:00',
        outPoint: '00:00:00:00',
        frameRate: 25,
        notes: ''
      }
      // Reset validation
      if (this.$refs.segmentObserver) {
        this.$refs.segmentObserver.reset()
      }
    },

    async saveSegment() {
      logger.debug('saveSegment called')
      logger.debug('Form data:', this.segmentForm)

      const isValid = await this.$refs.segmentObserver.validate()
      logger.debug('Validation result:', isValid)

      if (!isValid) {
        logger.debug('Validation failed, not saving')
        return
      }

      try {
        const payload = {
          episodeId: this.episodeId,
          segmentNumber: parseInt(this.segmentForm.segmentNumber),
          segmentType: this.segmentForm.segmentType,
          inPoint: this.segmentForm.inPoint,
          outPoint: this.segmentForm.outPoint,
          frameRate: parseInt(this.segmentForm.frameRate),
          notes: this.segmentForm.notes
        }

        let result
        if (this.editingSegment) {
          payload.segmentId = this.editingSegment.id
          result = await segmentsAPI.update(payload)
          this.$buefy.toast.open({
            message: this.composeRegenMessage('Segment updated', result),
            type: 'is-success',
            duration: 4000
          })
        } else {
          logger.debug('Creating segment with payload:', payload)
          result = await segmentsAPI.create(payload)
          logger.debug('Segment created, result:', result)
          this.$buefy.toast.open({
            message: this.composeRegenMessage('Segment created', result),
            type: 'is-success',
            duration: 4000
          })
        }

        logger.debug('Closing modal and reloading segments')
        this.closeModal()
        await this.loadSegments()
        logger.debug('Segments reloaded')
      } catch (error) {
        logger.error('Save segment error:', error)
        logger.error('Error response:', error.response || error.data)
        const message =
          error.response?.data?.message ||
          error.data?.message ||
          'Failed to save segment'
        this.$buefy.toast.open({
          message: message,
          type: 'is-danger',
          duration: 5000
        })
      }
    },

    confirmDelete(segment) {
      this.$buefy.dialog.confirm({
        title: 'Delete Segment',
        message: `Are you sure you want to delete Segment #${segment.segment_number}?`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deleteSegment(segment.id)
      })
    },

    async deleteSegment(segmentId) {
      try {
        const result = await segmentsAPI.remove(segmentId)
        this.$buefy.toast.open({
          message: this.composeRegenMessage('Segment deleted', result),
          type: 'is-success',
          duration: 4000
        })
        this.loadSegments()
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to delete segment',
          type: 'is-danger'
        })
      }
    },

    /**
     * Build a toast message that includes day-view sync info when the segment
     * change cascaded to scheduled instances. Surfaces the cascade to the operator
     * so they know "yes, your edit also updated the broadcast schedule."
     */
    composeRegenMessage(action, result) {
      if (result && result.scheduledInstancesAffected > 0) {
        const n = result.scheduledInstancesAffected
        return `${action} — updated ${n} scheduled instance${n > 1 ? 's' : ''} in day view`
      }
      if (result && result.regenerationWarning) {
        return `${action} — but day view sync failed. Day view and exports may be stale.`
      }
      return `${action} successfully`
    },

    async exportCSV() {
      try {
        const blob = await segmentsAPI.exportEpisodeCSV(this.episodeId)
        const filename = `episode_${this.episodeId}_segments_${Date.now()}.csv`
        segmentsAPI.downloadCSV(blob, filename)
        this.$buefy.toast.open({
          message: 'CSV exported successfully',
          type: 'is-success'
        })
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to export CSV',
          type: 'is-danger'
        })
      }
    },

    validateTimecode() {
      // This will be called on input to provide real-time feedback
      if (this.$refs.segmentObserver) {
        this.$refs.segmentObserver.validate()
      }
    },

    formatDuration(seconds) {
      if (!seconds) return '0s'
      const mins = Math.floor(seconds / 60)
      const secs = (seconds % 60).toFixed(2)
      return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`
    }
  }
}
</script>

<style scoped>
.segments-manager {
  margin-top: 2rem;
  padding: 1.5rem;
  background: #fafafa;
  border-radius: 6px;
}

.segments-table {
  color: #363636 !important;
}

.segments-table >>> td,
.segments-table >>> th {
  color: #363636 !important;
}

code {
  font-family: 'Courier New', monospace;
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 3px;
  color: #363636 !important;
}
</style>
